import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getHomepagePlays } from '../../../services/playsService';

const PlaysShowcase = () => {
  const [plays, setPlays] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPlays();
  }, []);

  const loadPlays = async () => {
    try {
      const data = await getHomepagePlays();
      // Randomize and select only 3 plays
      const shuffled = (data || [])?.sort(() => Math.random() - 0.5);
      const selectedPlays = shuffled?.slice(0, 3);
      setPlays(selectedPlays);
    } catch (error) {
      console.error('Error loading plays:', error);
      setError('Failed to load plays');
    } finally {
      setIsLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'medium':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'hard':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  if (isLoading) {
    return (
      <section className="py-24 lg:py-32 relative bg-black/20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mb-4"></div>
            <p className="text-white/70">Loading plays...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-24 lg:py-32 relative bg-black/20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
            <p className="text-white/70">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 lg:py-32 pb-12 lg:pb-16 relative bg-black/20">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Explore Shakespeare's <span className="gradient-text">Greatest Hits</span>
          </h2>
          <p className="text-base sm:text-lg text-white/70 max-w-3xl mx-auto">
            Dive into the world's most legendary plays with award-winning guidance, character breakdowns, and interactive performance tools.
          </p>
        </div>

        {plays?.length > 0 ? (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plays?.map((play) => (
                <div
                  key={play?.id}
                  className="modern-card group cursor-pointer hover:scale-105 transition-all duration-300 overflow-hidden p-0"
                >
                  <div className="relative h-48 overflow-hidden">
                    {play?.cover_image_url ? (
                      <img
                        src={play?.cover_image_url}
                        alt={`${play?.title} by ${play?.author}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                        <Icon name="BookOpen" size={48} className="text-white opacity-50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent opacity-80" />
                    <div className="absolute top-3 right-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(play?.difficulty)}`}>
                        {play?.difficulty || 'medium'}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                      {play?.title}
                    </h3>
                    {play?.description && (
                      <p className="text-white/60 text-sm mb-3 line-clamp-2">
                        {play?.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-white/60 text-sm">
                      <Icon name="BookOpen" size={16} />
                      <span>Start Reading</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <p className="text-white/70 text-lg italic">...and many more!</p>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <Icon name="BookOpen" size={64} className="text-white/30 mx-auto mb-4" />
            <p className="text-white/70">No plays available at the moment</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PlaysShowcase;